import React, { FC, useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { AdminLayout, AdminFilterTrigger, AdminFilterSidebar } from '../components'
import { useAdmin } from '../contexts/AdminContext'
import { Modal, ImageWithFallback, Select, TablePagination, Input } from '../../../shared/components'
import { buildImageUrl } from '../../../config'
import { useToast } from '../../../contexts/ToastContext'
import adminOrderService from '../../../services/adminOrderService'
import orderService from '../../../services/orderService'
import { AdminOrder, OrderDetail, OrderStatus, PaymentMethod, PaymentStatus } from '../../../types/api'
import { colors } from '../../../styles/design-system'
import {
  AccessTime as ClockIcon,
  CheckCircle as CheckCircleIcon,
  Settings as SettingsIcon,
  LocalShipping as ShippingIcon,
  Store as StoreIcon,
  LocationOn as LocationIcon,
  Bolt as BoltIcon,
  Description as DescriptionIcon,
  ContentCopy as CopyIcon,
  Receipt as ReceiptIcon,
  OpenInNew as OpenInNewIcon,
  Payment as PaymentIcon,
  Cancel as CancelIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Laptop as SystemIcon
} from '@mui/icons-material'
import {
  VerticalTimeline,
  VerticalTimelineElement
} from 'react-vertical-timeline-component'
import 'react-vertical-timeline-component/style.min.css'

// type OrderStatus = AdminOrder['status'] - Já importado do types/api

const CSS_HANDLES = [
  'adminOrders',
  'ordersHeader',
  'ordersTitle',
  'ordersSubtitle',
  'filtersSection',
  'filterGroup',
  'filterLabel',
  'filterSelect',
  'filterInput',
  'filterButton',
  'ordersTable',
  'tableHeader',
  'tableRow',
  'orderId',
  'orderCustomer',
  'orderDate',
  'orderTotal',
  'orderStatus',
  'orderPayment',
  'orderTracking',
  'orderActions',
  'statusBadge',
  'statusPending',
  'statusConfirmed',
  'statusProcessing',
  'statusShipped',
  'statusDelivered',
  'statusCancelled',
  'statusReturned',
  'viewButton',
  'editButton',
  'updateButton',
  'pagination',
  'paginationButton',
  'paginationInfo',
  'orderModal',
  'modalOverlay',
  'modalContent',
  'modalHeader',
  'modalTitle',
  'modalClose',
  'modalBody',
  'orderDetails',
  'customerInfo',
  'orderItems',
  'orderItem',
  'itemImage',
  'itemInfo',
  'itemName',
  'itemQuantity',
  'itemPrice',
  'orderSummary',
  'summaryRow',
  'summaryLabel',
  'summaryValue',
  'statusUpdate',
  'statusSelect',
  'trackingInput',
  'modalFooter',
  'cancelButton',
  'saveButton',
  'boletoModal',
  'boletoInfo',
  'barcodeBox',
  'copyButton'
] as const

const ITEMS_PER_PAGE = 10

const AdminOrders: FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const { refreshPendingOrders } = useAdmin()
  const [orders, setOrders] = useState<AdminOrder[]>([])
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: ITEMS_PER_PAGE,
    total: 0,
    totalPages: 1
  })
  const [isLoading, setIsLoading] = useState(true)
  const [isModalLoading, setIsModalLoading] = useState(false)
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false)

  type StatusFilter = 'all' | OrderStatus
  type OrderSortField =
    | 'createdAt'
    | 'total'
    | 'status'
    | 'orderNumber'
    | 'customerName'
    | 'paymentMethod'

  type Filters = {
    customer: string
    dateFrom: string
    dateTo: string
    minValue: string
    maxValue: string
    status: StatusFilter
    shippingMethod: 'all' | 'pickup' | 'delivery'
    trackingCode: string
  }

  const defaultFilters: Filters = {
    customer: '',
    dateFrom: '',
    dateTo: '',
    minValue: '',
    maxValue: '',
    status: 'all',
    shippingMethod: 'all',
    trackingCode: ''
  }

  const [filters, setFilters] = useState<Filters>(() => ({
    ...defaultFilters
  }))
  const [draftFilters, setDraftFilters] = useState<Filters>(() => ({
    ...defaultFilters
  }))
  const [sortState, setSortState] = useState<{ field: OrderSortField; order: 'asc' | 'desc' }>({
    field: 'createdAt',
    order: 'desc'
  })

  // Refs para evitar chamadas duplicadas
  const previousFiltersRef = useRef<string | null>(null)
  const previousPageRef = useRef<number>(1)
  const previousSortStateRef = useRef<string | null>(null)
  const isLoadingRef = useRef(false)

  useEffect(() => {
    let mounted = true

    const fetchOrders = async () => {
      // Verificar se realmente precisa carregar (debounce e validação)
      // Serializar filtros e sortState para comparação
      const filtersKey = JSON.stringify({
        customer: filters.customer.trim(),
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        minValue: filters.minValue,
        maxValue: filters.maxValue,
        status: filters.status,
        shippingMethod: filters.shippingMethod,
        trackingCode: filters.trackingCode.trim()
      })
      const sortStateKey = JSON.stringify({
        field: sortState.field,
        order: sortState.order
      })

      // Verificar se realmente mudou
      const filtersChanged = previousFiltersRef.current !== filtersKey
      // Usar uma variável local para página, pois o estado currentPage pode estar desatualizado
      const currentPageLocal = 1 
      const pageChanged = previousPageRef.current !== currentPageLocal
      const sortStateChanged = previousSortStateRef.current !== sortStateKey

      if (!filtersChanged && !pageChanged && !sortStateChanged && !isLoadingRef.current && previousFiltersRef.current === filtersKey) {
        return
      }

      isLoadingRef.current = true
      previousFiltersRef.current = filtersKey
      previousPageRef.current = currentPageLocal
      previousSortStateRef.current = sortStateKey

      setIsLoading(true)
      setError(null)

      try {
        const response = await adminOrderService.list({
          page: currentPageLocal,
          limit: ITEMS_PER_PAGE,
          customer: filters.customer.trim() || undefined,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
          minValue: filters.minValue ? parseFloat(filters.minValue) : undefined,
          maxValue: filters.maxValue ? parseFloat(filters.maxValue) : undefined,
          status: filters.status !== 'all' ? filters.status : undefined,
          shippingMethod: filters.shippingMethod !== 'all' ? filters.shippingMethod : undefined,
          trackingCode: filters.trackingCode.trim() || undefined,
          sortBy: sortState.field,
          sortOrder: sortState.order
        })

        if (mounted) {
          setOrders(response.orders)
          setPagination(response.pagination)
          setCurrentPage(response.pagination.page)
        }
      } catch (err) {
        console.error('Erro ao carregar pedidos administrativos:', err)
        if (mounted) {
          setOrders([])
          setPagination({
            page: currentPageLocal,
            limit: ITEMS_PER_PAGE,
            total: 0,
            totalPages: 1
          })
          setError('Não foi possível carregar os pedidos. Tente novamente.')
        }
        previousFiltersRef.current = null
        previousPageRef.current = 1
        previousSortStateRef.current = null
      } finally {
        if (mounted) {
          setIsLoading(false)
          isLoadingRef.current = false
        }
      }
    }

    fetchOrders()

    return () => {
      mounted = false
    }
  }, [filters, sortState])

  const hasActiveFilters =
    filters.status !== 'all' ||
    filters.customer.trim() !== '' ||
    filters.dateFrom !== '' ||
    filters.dateTo !== '' ||
    filters.minValue !== '' ||
    filters.maxValue !== '' ||
    filters.shippingMethod !== 'all' ||
    filters.trackingCode.trim() !== ''

  const handleOpenFilters = () => {
    setDraftFilters({ ...filters })
    setIsFilterSidebarOpen(true)
  }

  const handleCloseFilters = () => {
    setIsFilterSidebarOpen(false)
  }

  const handleDraftFilterChange = <Key extends keyof typeof draftFilters>(key: Key, value: (typeof draftFilters)[Key]) => {
    setDraftFilters((prev) => ({
      ...prev,
      [key]: value
    }))
  }

  const handleApplyFilters = () => {
    setFilters({ ...draftFilters })
    setCurrentPage(1)
    setIsFilterSidebarOpen(false)
  }

  const handleClearFilters = () => {
    const reset = { ...defaultFilters }
    setDraftFilters(reset)
    setFilters(reset)
    setCurrentPage(1)
  }

  const handleSort = (field: OrderSortField) => {
    setSortState((previous) => {
      const nextOrder = previous.field === field && previous.order === 'asc' ? 'desc' : 'asc'
      return {
        field,
        order: previous.field === field ? nextOrder : 'asc'
      }
    })
    setCurrentPage(1)
  }

  const renderSortIndicator = (field: OrderSortField) => {
    if (sortState.field !== field) {
      return (
        <span style={{ marginLeft: 6, color: colors.neutral.gray300 }} aria-hidden="true">
          ⇅
        </span>
      )
    }

    return (
      <span
        style={{
          marginLeft: 6,
          color: colors.primary.blue,
          display: 'inline-flex',
          alignItems: 'center'
        }}
        aria-hidden="true"
      >
        {sortState.order === 'asc' ? '↑' : '↓'}
      </span>
    )
  }

  const getAriaSort = (field: OrderSortField): 'none' | 'ascending' | 'descending' => {
    if (sortState.field !== field) {
      return 'none'
    }
    return sortState.order === 'asc' ? 'ascending' : 'descending'
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) {
      return '-'
    }
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) {
      return '-'
    }
    return date.toLocaleDateString('pt-BR')
  }

  const getStatusBadge = (status: OrderStatus | string | undefined | null) => {
    const normalizedStatus = (status ?? '').toString().toLowerCase() as OrderStatus

    const statusConfig: Record<string, { label: string; className: string }> = {
      pending: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800' },
      confirmed: { label: 'Confirmado', className: 'bg-blue-100 text-blue-800' },
      processing: { label: 'Processando', className: 'bg-purple-100 text-purple-800' },
      shipped: { label: 'Enviado', className: 'bg-indigo-100 text-indigo-800' },
      delivered: { label: 'Entregue', className: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Cancelado', className: 'bg-red-100 text-red-800' },
      returned: { label: 'Devolvido', className: 'bg-gray-100 text-gray-800' },
      ready_for_pickup: { label: 'Disponível para retirada', className: 'bg-blue-100 text-blue-800' }
    }

    const fallback = { label: 'Indefinido', className: 'bg-gray-100 text-gray-600' }
    const config = statusConfig[normalizedStatus as OrderStatus] ?? fallback

    return (
      <span className={`${handles.statusBadge} px-2 py-1 text-xs font-medium rounded-full ${config.className}`}>
        {config.label}
      </span>
    )
  }

  const getPaymentMethodLabel = (method: string) => {
    if (!method) return '—'
    
    // Normalizar o método para minúsculas para comparação
    const normalizedMethod = method.toLowerCase().trim()
    
    const methods: Record<string, string> = {
      pix: 'PIX',
      credit_card: 'Cartão de Crédito',
      'credit card': 'Cartão de Crédito',
      debit_card: 'Cartão de Débito',
      'debit card': 'Cartão de Débito',
      boleto: 'Boleto',
      'boleto bancário': 'Boleto'
    }
    
    return methods[normalizedMethod] || method
  }

  const handleViewOrder = async (order: AdminOrder) => {
    setIsModalOpen(true)
    setIsModalLoading(true)
    setSelectedOrder(order as OrderDetail)

    try {
      const response = await adminOrderService.getById(order.id)
      const detailedOrder = response.order

      setSelectedOrder({
        ...order,
        ...detailedOrder,
        customerName: detailedOrder.customerName ?? order.customerName,
        customerEmail: detailedOrder.customerEmail ?? order.customerEmail,
        shippingAddress: detailedOrder.shippingAddress ?? order.shippingAddress,
        billingAddress: detailedOrder.billingAddress ?? order.billingAddress,
        items: detailedOrder.items?.length ? detailedOrder.items : order.items
      } as OrderDetail)
    } catch (err) {
      console.error('Erro ao carregar detalhes do pedido:', err)
      setError('Não foi possível carregar os detalhes do pedido. Tente novamente.')
    } finally {
      setIsModalLoading(false)
    }
  }

  const loadOrders = useCallback(async (page: number, force = false) => {
    // Verificar se já está carregando para evitar chamadas simultâneas
    if (isLoadingRef.current && !force) {
      return
    }

    isLoadingRef.current = true
    setIsLoading(true)
    setError(null)

    try {
      const response = await adminOrderService.list({
        page,
        limit: ITEMS_PER_PAGE,
        customer: filters.customer.trim() || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        minValue: filters.minValue ? parseFloat(filters.minValue) : undefined,
        maxValue: filters.maxValue ? parseFloat(filters.maxValue) : undefined,
        status: filters.status !== 'all' ? filters.status : undefined,
        shippingMethod: filters.shippingMethod !== 'all' ? filters.shippingMethod : undefined,
        trackingCode: filters.trackingCode.trim() || undefined,
        sortBy: sortState.field,
        sortOrder: sortState.order
      })

      setOrders(response.orders)
      setPagination(response.pagination)
      setCurrentPage(response.pagination.page)
    } catch (err) {
      console.error('Erro ao carregar pedidos administrativos:', err)
      setOrders([])
      setPagination({
        page,
        limit: ITEMS_PER_PAGE,
        total: 0,
        totalPages: 1
      })
      setError('Não foi possível carregar os pedidos. Tente novamente.')
    } finally {
      setIsLoading(false)
      isLoadingRef.current = false
    }
  }, [filters, sortState.field, sortState.order])

  const handleUpdateStatus = async (
    orderId: string,
    newStatus: OrderStatus,
    trackingCode?: string
  ) => {
    setIsUpdatingStatus(true)
    setError(null)

    try {
      await orderService.updateStatus(orderId, {
        status: newStatus,
        trackingCode
      })
      setIsModalOpen(false)
      setSelectedOrder(null)
      // Forçar atualização da tabela após mudar o status
      await loadOrders(currentPage, true)
      // Atualizar contador de pedidos pendentes na sidebar
      await refreshPendingOrders()
    } catch (err) {
      console.error('Erro ao atualizar status do pedido:', err)
      setError('Não foi possível atualizar o status do pedido. Tente novamente.')
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  // Paginação
  const totalPages = Math.max(pagination.totalPages, 1)
  const currentOrders = orders
  const displayStart =
    pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1
  const displayEnd =
    pagination.total === 0
      ? 0
      : Math.min(pagination.page * pagination.limit, pagination.total)
  const statusSummary =
    filters.status === 'all'
      ? null
      : `status: ${
          {
            pending: 'pendente',
            confirmed: 'confirmado',
            processing: 'processando',
            shipped: 'enviado',
            delivered: 'entregue',
            cancelled: 'cancelado',
            returned: 'devolvido',
            ready_for_pickup: 'disponível para retirada'
          }[filters.status as string]
        }`
  const activeFiltersDescriptions = [
    statusSummary,
    filters.customer.trim() ? `cliente: "${filters.customer.trim()}"` : null,
    filters.dateFrom || filters.dateTo
      ? `data: ${filters.dateFrom || '...'} até ${filters.dateTo || '...'}`
      : null,
    filters.minValue || filters.maxValue
      ? `valor: ${filters.minValue ? `R$ ${filters.minValue}` : '...'} até ${filters.maxValue ? `R$ ${filters.maxValue}` : '...'}`
      : null,
    filters.shippingMethod !== 'all'
      ? `tipo: ${
          {
            pickup: 'retirada',
            delivery: 'entrega'
          }[filters.shippingMethod]
        }`
      : null,
    filters.trackingCode.trim() ? `rastreio: "${filters.trackingCode.trim()}"` : null
  ]
    .filter(Boolean)
    .join(' • ')
  const isApplyDisabled =
    draftFilters.customer === filters.customer &&
    draftFilters.dateFrom === filters.dateFrom &&
    draftFilters.dateTo === filters.dateTo &&
    draftFilters.minValue === filters.minValue &&
    draftFilters.maxValue === filters.maxValue &&
    draftFilters.status === filters.status &&
    draftFilters.shippingMethod === filters.shippingMethod &&
    draftFilters.trackingCode === filters.trackingCode

  return (
    <AdminLayout>
      <>
        <div className={`${handles.adminOrders}`}>
          {/* Header */}
          <div className={`${handles.ordersHeader} mb-8`}>
            <h1 className={`${handles.ordersTitle} mb-2 text-3xl font-bold text-gray-900`}>
              Gestão de Pedidos
            </h1>
            <p className={`${handles.ordersSubtitle} text-lg text-gray-600`}>
              Acompanhe e gerencie todos os pedidos da loja
            </p>
          </div>

          {/* Barra de ações */}
          <div className={`${handles.filtersSection} mb-8 rounded-lg bg-white p-6 shadow-sm`}>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm text-gray-600">
                  Exibindo{' '}
                  <span className="font-semibold text-gray-900">
                    {pagination.total === 0 ? 0 : displayStart} - {displayEnd}
                  </span>{' '}
                  de{' '}
                  <span className="font-semibold text-gray-900">{pagination.total}</span> pedidos
                </p>
                {hasActiveFilters && (
                  <p className="mt-1 text-xs text-blue-600">
                    Filtros aplicados: {activeFiltersDescriptions}
                  </p>
                )}
              </div>
              <AdminFilterTrigger
                onClick={handleOpenFilters}
                hasActiveFilters={hasActiveFilters}
              />
            </div>
          </div>

          {/* Tabela de Pedidos */}
          <div className="overflow-hidden rounded-lg bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className={`${handles.ordersTable} w-full`}>
                <thead className={`${handles.tableHeader} bg-gray-50`}>
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      aria-sort={getAriaSort('orderNumber')}
                    >
                      <button
                        type="button"
                        onClick={() => handleSort('orderNumber')}
                        className="flex items-center gap-1 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:text-blue-600"
                      >
                        ID do Pedido
                        {renderSortIndicator('orderNumber')}
                      </button>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      aria-sort={getAriaSort('customerName')}
                    >
                      <button
                        type="button"
                        onClick={() => handleSort('customerName')}
                        className="flex items-center gap-1 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:text-blue-600"
                      >
                        Cliente
                        {renderSortIndicator('customerName')}
                      </button>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      aria-sort={getAriaSort('createdAt')}
                    >
                      <button
                        type="button"
                        onClick={() => handleSort('createdAt')}
                        className="flex items-center gap-1 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:text-blue-600"
                      >
                        Data
                        {renderSortIndicator('createdAt')}
                      </button>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      aria-sort={getAriaSort('total')}
                    >
                      <button
                        type="button"
                        onClick={() => handleSort('total')}
                        className="flex items-center gap-1 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:text-blue-600"
                      >
                        Total
                        {renderSortIndicator('total')}
                      </button>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      aria-sort={getAriaSort('status')}
                    >
                      <button
                        type="button"
                        onClick={() => handleSort('status')}
                        className="flex items-center gap-1 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:text-blue-600"
                      >
                        Status
                        {renderSortIndicator('status')}
                      </button>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      aria-sort={getAriaSort('paymentMethod')}
                    >
                      <button
                        type="button"
                        onClick={() => handleSort('paymentMethod')}
                        className="flex items-center gap-1 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:text-blue-600"
                      >
                        Pagamento
                        {renderSortIndicator('paymentMethod')}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Tipo de Entrega
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Código de Rastreio
                    </th>
                    <th className="sticky right-0 z-20 bg-gray-50 px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 shadow-[inset_1px_0_0_0_rgb(226,232,240)]">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {isLoading ? (
                    Array.from({ length: Math.min(ITEMS_PER_PAGE, 5) }).map((_, index) => (
                      <tr key={`order-skeleton-${index}`} className="animate-pulse">
                        <td className="px-6 py-4">
                          <div className="h-4 w-24 rounded bg-gray-200" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="mb-2 h-4 w-32 rounded bg-gray-200" />
                          <div className="h-3 w-40 rounded bg-gray-200" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 w-20 rounded bg-gray-200" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 w-16 rounded bg-gray-200" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-5 w-20 rounded-full bg-gray-200" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 w-24 rounded bg-gray-200" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 w-32 rounded bg-gray-200" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <div className="h-9 w-9 rounded bg-gray-200" />
                            <div className="h-9 w-9 rounded bg-gray-200" />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : error ? (
                    <tr>
                      <td colSpan={9} className="px-6 py-10 text-center text-sm text-red-600">
                        {error}
                      </td>
                    </tr>
                  ) : currentOrders.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-6 py-10 text-center text-sm text-gray-500">
                        Nenhum pedido encontrado para os filtros selecionados.
                      </td>
                    </tr>
                  ) : (
                    currentOrders.map((order) => (
                      <tr key={order.id} className={`${handles.tableRow} hover:bg-gray-50`}>
                        <td className={`${handles.orderId} whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900`}>
                          {order.orderNumber || order.id}
                        </td>
                        <td className={`${handles.orderCustomer} whitespace-nowrap px-6 py-4`}>
                          <div className="text-sm font-medium text-gray-900">
                            {order.customerName || order.shippingAddress?.name || 'Cliente'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {order.customerEmail || '—'}
                          </div>
                        </td>
                        <td className={`${handles.orderDate} whitespace-nowrap px-6 py-4 text-sm text-gray-900`}>
                          {formatDate(order.createdAt || order.date)}
                        </td>
                        <td className={`${handles.orderTotal} whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900`}>
                          {formatPrice(order.total)}
                        </td>
                        <td className={`${handles.orderStatus} whitespace-nowrap px-6 py-4`}>
                          {getStatusBadge(order.status)}
                        </td>
                        <td className={`${handles.orderPayment} whitespace-nowrap px-6 py-4 text-sm text-gray-900`}>
                          {getPaymentMethodLabel(order.paymentMethod)}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          {order.shippingMethod?.toLowerCase() === 'pickup' ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-800">
                              <StoreIcon className="h-3.5 w-3.5" />
                              Retirada
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800">
                              <ShippingIcon className="h-3.5 w-3.5" />
                              Entrega
                            </span>
                          )}
                        </td>
                        <td className={`${handles.orderTracking} whitespace-nowrap px-6 py-4 text-sm text-gray-900`}>
                          {order.trackingCode ? (
                            <span className="font-mono text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded border border-blue-200">
                              {order.trackingCode}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className={`${handles.orderActions} sticky right-0 z-10 whitespace-nowrap bg-white px-6 py-4 text-sm font-medium shadow-[inset_1px_0_0_0_rgb(226,232,240)]`}>
                          <div className="flex justify-end">
                            <button
                              onClick={() => handleViewOrder(order)}
                              title="Visualizar pedido"
                              aria-label="Visualizar pedido"
                              className={`${handles.viewButton} inline-flex h-9 w-9 items-center justify-center rounded-md bg-yellow-400 text-gray-900 shadow-sm transition-colors hover:bg-yellow-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:ring-offset-2`}
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span className="sr-only">Visualizar pedido</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            <TablePagination
              currentPage={pagination.page}
              totalPages={totalPages}
              totalItems={pagination.total}
              itemsPerPage={pagination.limit}
              onPageChange={(page) => {
                setCurrentPage(page)
                loadOrders(page, true)
              }}
              isLoading={isLoading}
              infoLabel="pedidos"
              className={handles.pagination}
            />
          </div>
        </div>

        {/* Modal de Detalhes do Pedido */}
        {isModalOpen && selectedOrder && (
          <OrderModal
            order={selectedOrder}
            onUpdateStatus={handleUpdateStatus}
            onClose={() => {
              setIsModalOpen(false)
              setSelectedOrder(null)
              setIsModalLoading(false)
            }}
            isLoading={isModalLoading}
            isUpdating={isUpdatingStatus}
          />
        )}
        <AdminFilterSidebar
          isOpen={isFilterSidebarOpen}
          onClose={handleCloseFilters}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          isApplyDisabled={isApplyDisabled}
          applyLabel="Aplicar filtros"
          clearLabel="Limpar filtros"
        >
          <div className="space-y-6">
            <div>
              <label
                htmlFor="admin-orders-filter-customer"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Cliente
              </label>
              <Input
                id="admin-orders-filter-customer"
                type="text"
                value={draftFilters.customer}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleDraftFilterChange('customer', event.target.value)
                }
                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm"
                placeholder="Nome ou CPF/CNPJ"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="admin-orders-filter-date-from"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Data Inicial
                </label>
                <Input
                  id="admin-orders-filter-date-from"
                  type="date"
                  value={draftFilters.dateFrom || ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleDraftFilterChange('dateFrom', event.target.value)
                  }
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm"
                />
              </div>
              <div>
                <label
                  htmlFor="admin-orders-filter-date-to"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Data Final
                </label>
                <Input
                  id="admin-orders-filter-date-to"
                  type="date"
                  value={draftFilters.dateTo || ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleDraftFilterChange('dateTo', event.target.value)
                  }
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="admin-orders-filter-min-value"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Valor Mínimo
                </label>
                <Input
                  id="admin-orders-filter-min-value"
                  type="number"
                  step="0.01"
                  min="0"
                  value={draftFilters.minValue}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleDraftFilterChange('minValue', event.target.value)
                  }
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label
                  htmlFor="admin-orders-filter-max-value"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Valor Máximo
                </label>
                <Input
                  id="admin-orders-filter-max-value"
                  type="number"
                  step="0.01"
                  min="0"
                  value={draftFilters.maxValue}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleDraftFilterChange('maxValue', event.target.value)
                  }
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="admin-orders-filter-status"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Status
              </label>
              <Select
                id="admin-orders-filter-status"
                value={draftFilters.status}
                onChange={(value) =>
                  handleDraftFilterChange('status', value as StatusFilter)
                }
                options={[
                  { value: 'all', label: 'Todos os status' },
                  { value: 'pending', label: 'Pendente' },
                  { value: 'confirmed', label: 'Confirmado' },
                  { value: 'processing', label: 'Processando' },
                  { value: 'shipped', label: 'Enviado' },
                  { value: 'ready_for_pickup', label: 'Disponível para retirada' },
                  { value: 'delivered', label: 'Entregue' },
                  { value: 'cancelled', label: 'Cancelado' },
                  { value: 'returned', label: 'Devolvido' }
                ]}
                placeholder="Selecione o status"
                className="w-full"
              />
            </div>

            <div>
              <label
                htmlFor="admin-orders-filter-shipping-method"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Tipo de Entrega
              </label>
              <Select
                id="admin-orders-filter-shipping-method"
                value={draftFilters.shippingMethod}
                onChange={(value) =>
                  handleDraftFilterChange('shippingMethod', value as 'all' | 'pickup' | 'delivery')
                }
                options={[
                  { value: 'all', label: 'Todos' },
                  { value: 'pickup', label: 'Retirada' },
                  { value: 'delivery', label: 'Entrega' }
                ]}
                placeholder="Selecione o tipo"
                className="w-full"
              />
            </div>

            <div>
              <label
                htmlFor="admin-orders-filter-tracking-code"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Código de Rastreio
              </label>
                <Input
                  id="admin-orders-filter-tracking-code"
                  type="text"
                  value={draftFilters.trackingCode}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleDraftFilterChange('trackingCode', event.target.value)
                  }
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm"
                  placeholder="BR123456789"
                />
            </div>
          </div>
        </AdminFilterSidebar>
      </>
    </AdminLayout>
  )
}

// Componente Modal para visualizar Boleto
interface BoletoModalProps {
  isOpen: boolean
  onClose: () => void
  boletoUrl?: string
  boletoBarcode?: string
  dueDate?: string
  amount: number
}

const BoletoModal: FC<BoletoModalProps> = ({ isOpen, onClose, boletoUrl, boletoBarcode, dueDate, amount }) => {
  const [copied, setCopied] = useState(false)

  const handleCopyBarcode = () => {
    if (boletoBarcode) {
      navigator.clipboard.writeText(boletoBarcode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—'
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  if (!isOpen) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Boleto Bancário"
      size="md"
      overlayClassName="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
      contentClassName="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
      headerClassName="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50"
      titleClassName="text-lg font-semibold text-gray-800 flex items-center gap-2"
      closeButtonClassName="text-gray-400 hover:text-gray-600 transition-colors"
      bodyClassName="p-0"
      footer={null}
    >
      <div className="p-6 space-y-6">
        {/* Valor e Vencimento */}
        <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg border border-blue-100">
          <div>
            <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Valor do Documento</p>
            <p className="text-xl font-bold text-blue-900">{formatPrice(amount)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Vencimento</p>
            <p className="text-lg font-semibold text-blue-900">{formatDate(dueDate)}</p>
          </div>
        </div>

        {/* Linha Digitável */}
        {boletoBarcode && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 block">Linha Digitável</label>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono text-sm text-gray-600 break-all">
                {boletoBarcode}
              </div>
              <button
                onClick={handleCopyBarcode}
                className="flex items-center justify-center h-full p-3 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-transparent hover:border-blue-100"
                title="Copiar código"
              >
                {copied ? <CheckCircleIcon className="h-5 w-5" /> : <CopyIcon className="h-5 w-5" />}
              </button>
            </div>
            {copied && <p className="text-xs text-green-600 font-medium">Código copiado!</p>}
          </div>
        )}

        {/* Ações */}
        <div className="grid grid-cols-2 gap-3 pt-2">
           <button
            onClick={onClose}
             className="w-full py-2.5 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
           >
             Fechar
           </button>
           {boletoUrl && (
             <a
               href={boletoUrl}
               target="_blank"
               rel="noopener noreferrer"
               className="w-full py-2.5 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
             >
               <OpenInNewIcon className="h-4 w-4" />
               Visualizar PDF
             </a>
           )}
        </div>
      </div>
    </Modal>
  )
}

// Componente Modal para detalhes do pedido
interface OrderModalProps {
  order: OrderDetail
  onUpdateStatus: (orderId: string, status: OrderStatus, trackingCode?: string) => void
  onClose: () => void
  isLoading: boolean
  isUpdating: boolean
}

const OrderModal: FC<OrderModalProps> = ({ order, onUpdateStatus, onClose, isLoading, isUpdating }) => {
  const handles = useCssHandles(CSS_HANDLES)
  const { showError } = useToast()
  const [newStatus, setNewStatus] = useState<OrderStatus>(order.status)
  const [trackingCode, setTrackingCode] = useState(order.trackingCode || '')
  const [activeTab, setActiveTab] = useState<'summary' | 'products' | 'progress' | 'movements' | 'info'>('summary')
  const [showBoletoModal, setShowBoletoModal] = useState(false)

  // Encontrar transação de boleto (com flexibilidade nos nomes dos campos)
  const boletoTransaction = order.transactions?.find(t => 
    (t.paymentMethod === PaymentMethod.BOLETO || t.paymentMethod === 'BOLETO' || t.paymentMethod === 'boleto') && 
    (t.boletoUrl || t.boletoBarcode || t.authorizationCode) // authorizationCode às vezes carrega a URL em alguns gateways
  )

  const canShowBoleto = 
    (order.paymentMethod?.toLowerCase() === PaymentMethod.BOLETO || order.paymentMethod?.toLowerCase() === 'boleto bancário') && 
    (order.paymentStatus?.toLowerCase() === PaymentStatus.PENDING || order.status?.toLowerCase() === OrderStatus.PENDING) &&
    !!boletoTransaction

  const getStatusBadgeModal = (status: OrderStatus | string | undefined | null) => {
    const normalizedStatus = (status ?? '').toString().toLowerCase() as OrderStatus

    const statusConfig: Record<string, { label: string; className: string }> = {
      [OrderStatus.PENDING]: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800' },
      [OrderStatus.CONFIRMED]: { label: 'Confirmado', className: 'bg-blue-100 text-blue-800' },
      [OrderStatus.PROCESSING]: { label: 'Processando', className: 'bg-purple-100 text-purple-800' },
      [OrderStatus.SHIPPED]: { label: 'Enviado', className: 'bg-indigo-100 text-indigo-800' },
      [OrderStatus.DELIVERED]: { label: 'Entregue', className: 'bg-green-100 text-green-800' },
      [OrderStatus.CANCELLED]: { label: 'Cancelado', className: 'bg-red-100 text-red-800' },
      [OrderStatus.RETURNED]: { label: 'Devolvido', className: 'bg-gray-100 text-gray-800' },
      [OrderStatus.READY_FOR_PICKUP]: { label: 'Disponível para retirada', className: 'bg-blue-100 text-blue-800' }
    }

    const fallback = { label: 'Indefinido', className: 'bg-gray-100 text-gray-600' }
    const config = statusConfig[normalizedStatus as OrderStatus] ?? fallback

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.className}`}>
        {config.label}
      </span>
    )
  }

  const getPaymentMethodLabelModal = (method: string) => {
    if (!method) return '—'
    
    const normalizedMethod = method.toLowerCase().trim()
    
    const methods: Record<string, string> = {
      [PaymentMethod.PIX]: 'PIX',
      [PaymentMethod.CREDIT_CARD]: 'Cartão de Crédito',
      'credit card': 'Cartão de Crédito',
      [PaymentMethod.DEBIT_CARD]: 'Cartão de Débito',
      'debit card': 'Cartão de Débito',
      [PaymentMethod.BOLETO]: 'Boleto',
      'boleto bancário': 'Boleto'
    }
    
    return methods[normalizedMethod] || method
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '—'
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return '—'
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const customerName = order.customerName || order.shippingAddress?.name || 'Cliente'
  const customerEmail = order.customerEmail || '—'
  const shippingAddress = order.shippingAddress
  const items = order.items ?? []
  const logs = order.logs || []

  // Separar produtos por tipo de entrega
  const isPickup = order.shippingMethod === 'pickup'
  const shippingItems = items.filter(item => {
    const product = item.product as { requiresShipping?: boolean } | undefined
    return product?.requiresShipping !== false && !isPickup
  })
  const pickupItems = items.filter(item => {
    const product = item.product as { requiresShipping?: boolean } | undefined
    return product?.requiresShipping === false || isPickup
  })

  const currentStatusSteps = useMemo(() => {
    const steps = [
      { key: 'pending', label: 'Pendente', icon: ClockIcon, color: '#fbbf24' },
      { key: 'confirmed', label: 'Confirmado', icon: CheckCircleIcon, color: '#3b82f6' },
      { key: 'processing', label: 'Processando', icon: SettingsIcon, color: '#8b5cf6' },
    ]

    if (isPickup) {
      steps.push({ key: 'ready_for_pickup', label: 'Disponível', icon: StoreIcon, color: '#3b82f6' })
      steps.push({ key: 'delivered', label: 'Retirado', icon: CheckCircleIcon, color: '#10b981' })
    } else {
      steps.push({ key: 'shipped', label: 'Enviado', icon: ShippingIcon, color: '#6366f1' })
      steps.push({ key: 'delivered', label: 'Entregue', icon: CheckCircleIcon, color: '#10b981' })
    }

    return steps
  }, [isPickup])

  const currentStatusIndex = currentStatusSteps.findIndex((step: any) => step.key === order.status.toLowerCase())
  const timelineSteps = currentStatusSteps.map((step: any, index: number) => {
    const isCompleted = index <= currentStatusIndex && currentStatusIndex >= 0
    const isCurrent = index === currentStatusIndex
    return { ...step, isCompleted, isCurrent }
  })

  const canSinalizePickup = isPickup && (order.status === 'confirmed' || order.status === 'processing')

  const handleSinalizePickup = () => {
    onUpdateStatus(order.id, 'ready_for_pickup' as OrderStatus)
  }

  const handleSave = () => {
    if (isUpdating || isLoading) {
      return
    }

    // Validação: se o status for "Enviado" (shipped), o código de rastreamento é obrigatório
    if (newStatus === 'shipped' && !trackingCode.trim()) {
      showError(
        'Código de rastreio obrigatório',
        'Por favor, preencha o código de rastreio antes de alterar o status para "Enviado".'
      )
      return
    }

    onUpdateStatus(order.id, newStatus, trackingCode)
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Pedido ${order.orderNumber || ''}`}
      size="xl"
      overlayClassName={`${handles.orderModal} ${handles.modalOverlay}`}
      contentClassName={`${handles.modalContent} w-full max-w-4xl`}
      headerClassName={handles.modalHeader}
      titleClassName={handles.modalTitle}
      closeButtonClassName={handles.modalClose}
      bodyClassName={handles.modalBody}
      bodyStyle={{ padding: 0 }}
      footer={
        <div className="flex w-full items-center justify-between">
          <div className="flex gap-2">
            {canShowBoleto && (
              <button
                type="button"
                onClick={() => setShowBoletoModal(true)}
                className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors"
              >
                <ReceiptIcon className="h-4 w-4" />
                Visualizar Boleto
              </button>
            )}
            {canSinalizePickup && (
              <button
                type="button"
                onClick={handleSinalizePickup}
                disabled={isUpdating || isLoading}
                className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm font-medium text-green-700 hover:bg-green-100 transition-colors"
              >
                <StoreIcon className="h-4 w-4" />
                Sinalizar Retirada
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className={`${handles.cancelButton} bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors`}
            >
              Fechar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isUpdating || isLoading}
              className={`${handles.saveButton} bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50`}
            >
              {isUpdating ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </div>
      }
    >
      {isLoading ? (
        <div className="space-y-6 p-6">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {Array.from({ length: 2 }).map((_, index) => (
              <div key={`modal-skeleton-${index}`} className="space-y-4 rounded-lg border border-gray-100 p-4">
                <div className="h-5 w-40 animate-pulse rounded bg-gray-200" />
                <div className="h-4 w-60 animate-pulse rounded bg-gray-200" />
                <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
                <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-0">
          {/* Cabeçalho do Pedido - Sempre visível */}
          <div className="border-b border-gray-200 bg-white p-5">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Criado em {formatDateTime(order.createdAt)}</p>
              </div>
              <div className="flex items-center gap-2">
                {isPickup ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800">
                    <StoreIcon className="h-3.5 w-3.5" />
                    Retirada na Loja
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
                    <ShippingIcon className="h-3.5 w-3.5" />
                    Entrega
                  </span>
                )}
                {getStatusBadgeModal(order.status)}
              </div>
            </div>
          </div>

          {/* Abas */}
          <div className="border-b border-gray-200 bg-white px-5">
            <div className="flex space-x-1">
              {[
                { id: 'summary', label: 'Resumo' },
                { id: 'products', label: 'Produtos' },
                { id: 'progress', label: 'Progresso' },
                { id: 'movements', label: 'Movimentações' },
                { id: 'info', label: 'Informações' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Conteúdo das Abas */}
          <div className="p-6">
            {activeTab === 'summary' && (
              <div className="space-y-6">
                {/* Valores Financeiros */}
                <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900">Resumo Financeiro</h3>
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500">Subtotal</p>
                      <p className="text-lg font-semibold text-gray-900">{formatPrice(order.subtotal ?? 0)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500">Desconto</p>
                      <p className="text-lg font-semibold text-green-600">-{formatPrice(order.discount ?? 0)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500">Frete</p>
                      <p className="text-lg font-semibold text-gray-900">{formatPrice(order.shipping ?? 0)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500">Total</p>
                      <p className="text-xl font-bold text-blue-600">{formatPrice(order.total ?? 0)}</p>
                    </div>
                  </div>
                </div>

                {/* Atualização de Status */}
                <div className="mx-auto w-full max-w-lg">
                  <div className="inline-flex w-full flex-col gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-2 sm:min-w-[140px]">
                      <SettingsIcon className="h-4 w-4 shrink-0 text-gray-500" />
                      <span className="text-xs font-medium text-gray-700">Atualizar:</span>
                    </div>
                    <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
                      <div className="flex-1">
                        <Select
                          value={newStatus}
                          onChange={(value) => setNewStatus(value as OrderStatus)}
                          options={[
                            { value: 'pending', label: 'Pendente' },
                            { value: 'confirmed', label: 'Confirmado' },
                            { value: 'processing', label: 'Processando' },
                            { value: 'shipped', label: 'Enviado' },
                            { value: 'delivered', label: 'Entregue' },
                            { value: 'cancelled', label: 'Cancelado' },
                            { value: 'returned', label: 'Devolvido' }
                          ]}
                          placeholder="Status"
                          className={`${handles.statusSelect} w-full`}
                        />
                      </div>
                      {!isPickup && (
                        <div className="flex-1">
                          <Input
                            type="text"
                            value={trackingCode}
                            onChange={(e) => setTrackingCode(e.target.value)}
                            placeholder="Código de rastreamento"
                            className={`${handles.trackingInput} w-full rounded-lg border ${
                              newStatus === 'shipped' && !trackingCode.trim()
                                ? 'border-red-300 bg-red-50'
                                : 'border-gray-300'
                            } px-3 py-2 text-sm transition-colors`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'products' && (
              <div className="space-y-4">
                {shippingItems.length > 0 && (
                  <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="mb-4 flex items-center gap-2">
                      <ShippingIcon className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Produtos para Envio</h3>
                    </div>
                    <div className="space-y-3">
                      {shippingItems.map((item, index) => {
                        const product = item.product as any
                        const productName = item.productName || (product?.name ?? 'Produto')
                        const productImage = buildImageUrl(item.productImage || product?.images?.[0]?.imageUrl)
                        const unitPrice = item.price ?? item.unitPrice ?? 0

                        return (
                          <div key={index} className="flex items-center gap-3 rounded-lg bg-gray-50 p-3">
                            <ImageWithFallback
                              src={productImage}
                              alt={productName}
                              className="h-16 w-16 rounded-lg object-cover"
                              fallbackWrapperClassName="flex h-16 w-16 items-center justify-center rounded-lg bg-gradient-to-br from-gray-50 to-gray-100"
                              fallbackIconClassName="h-8 w-8 text-gray-300"
                            />
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{productName}</p>
                              <p className="text-sm text-gray-600">
                                {item.brand} • Qtd: {item.quantity} • {formatPrice(unitPrice)} un.
                              </p>
                            </div>
                            <p className="font-semibold text-gray-900">{formatPrice(unitPrice * item.quantity)}</p>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {pickupItems.length > 0 && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-5 shadow-sm">
                    <div className="mb-4 flex items-center gap-2">
                      <StoreIcon className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Produtos para Retirada</h3>
                    </div>
                    <div className="space-y-3">
                      {pickupItems.map((item, index) => {
                        const product = item.product as any
                        const productName = item.productName || (product?.name ?? 'Produto')
                        const productImage = buildImageUrl(item.productImage || product?.images?.[0]?.imageUrl)
                        const unitPrice = item.price ?? item.unitPrice ?? 0

                        return (
                          <div key={index} className="flex items-center gap-3 rounded-lg bg-white p-3">
                            <ImageWithFallback
                              src={productImage}
                              alt={productName}
                              className="h-16 w-16 rounded-lg object-cover"
                              fallbackWrapperClassName="flex h-16 w-16 items-center justify-center rounded-lg bg-gradient-to-br from-gray-50 to-gray-100"
                              fallbackIconClassName="h-8 w-8 text-gray-300"
                            />
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{productName}</p>
                              <p className="text-sm text-gray-600">
                                {item.brand} • Qtd: {item.quantity} • {formatPrice(unitPrice)} un.
                              </p>
                            </div>
                            <p className="font-semibold text-gray-900">{formatPrice(unitPrice * item.quantity)}</p>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {shippingItems.length === 0 && pickupItems.length === 0 && (
                  <div className="rounded-lg border border-gray-200 bg-white p-5 text-center text-gray-500">
                    Nenhum produto encontrado neste pedido.
                  </div>
                )}
              </div>
            )}

            {activeTab === 'progress' && (
              <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">Progresso do Pedido</h3>
                <VerticalTimeline layout="1-column-left" lineColor="#e5e7eb">
                  {timelineSteps.map((step: any) => {
                    const IconComponent = step.icon
                    const stepLog = logs.find(
                      (log) =>
                        log.action === 'status_changed' &&
                        log.status.toLowerCase() === step.key
                    )
                    
                    return (
                      <VerticalTimelineElement
                        key={step.key}
                        className="vertical-timeline-element--work"
                        contentStyle={{
                          background: step.isCurrent ? '#eff6ff' : step.isCompleted ? '#f0fdf4' : '#f9fafb',
                          border: step.isCurrent ? '2px solid #3b82f6' : step.isCompleted ? '2px solid #10b981' : '2px solid #e5e7eb',
                          borderRadius: '8px',
                          padding: '16px'
                        }}
                        contentArrowStyle={{
                          borderRight: step.isCompleted || step.isCurrent ? `7px solid ${step.isCompleted ? '#10b981' : '#3b82f6'}` : '7px solid #e5e7eb'
                        }}
                        iconStyle={{
                          background: step.isCompleted ? step.color : step.isCurrent ? step.color : '#9ca3af',
                          color: '#fff',
                          boxShadow: step.isCurrent ? `0 0 0 4px ${step.color}33` : 'none'
                        }}
                        icon={<IconComponent />}
                        date={stepLog ? formatDateTime(stepLog.createdAt) : ''}
                        dateClassName="text-gray-600 text-xs"
                      >
                        <div className="flex items-center justify-between">
                          <h4 className={`text-sm font-semibold ${step.isCompleted || step.isCurrent ? 'text-gray-900' : 'text-gray-500'}`}>
                            {step.label}
                          </h4>
                          {step.isCurrent && (
                            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                              Atual
                            </span>
                          )}
                        </div>
                        {stepLog?.description && (
                          <p className="mt-1 text-xs text-gray-600">{stepLog.description}</p>
                        )}
                      </VerticalTimelineElement>
                    )
                  })}
                </VerticalTimeline>
              </div>
            )}

            {activeTab === 'movements' && (
              <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <div className="mb-6 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Histórico de Movimentações</h3>
                  <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                    {logs.length} {logs.length === 1 ? 'registro' : 'registros'}
                  </span>
                </div>

                {logs.length > 0 ? (
                  <div className="relative space-y-0 before:absolute before:left-[19px] before:top-2 before:h-[calc(100%-16px)] before:w-0.5 before:bg-gray-100">
                    {logs.map((log, index) => {
                      const isLatest = index === 0
                      
                      const getActionInfo = (action: string) => {
                        switch (action) {
                          case 'status_changed':
                            return {
                              label: 'Alteração de Status',
                              icon: <SettingsIcon className="h-5 w-5" />,
                              color: 'bg-blue-100 text-blue-600'
                            }
                          case 'tracking_updated':
                            return {
                              label: 'Rastreamento Atualizado',
                              icon: <ShippingIcon className="h-5 w-5" />,
                              color: 'bg-indigo-100 text-indigo-600'
                            }
                          case 'payment_received':
                          case 'payment_confirmed':
                            return {
                              label: 'Pagamento Confirmado',
                              icon: <PaymentIcon className="h-5 w-5" />,
                              color: 'bg-green-100 text-green-600'
                            }
                          case 'payment_failed':
                            return {
                              label: 'Falha no Pagamento',
                              icon: <CancelIcon className="h-5 w-5" />,
                              color: 'bg-red-100 text-red-600'
                            }
                          case 'order_created':
                            return {
                              label: 'Pedido Criado',
                              icon: <ReceiptIcon className="h-5 w-5" />,
                              color: 'bg-gray-100 text-gray-600'
                            }
                          case 'order_cancelled':
                            return {
                              label: 'Pedido Cancelado',
                              icon: <CancelIcon className="h-5 w-5" />,
                              color: 'bg-red-100 text-red-600'
                            }
                          case 'note_added':
                            return {
                              label: 'Nota Adicionada',
                              icon: <DescriptionIcon className="h-5 w-5" />,
                              color: 'bg-yellow-100 text-yellow-600'
                            }
                          default:
                            return {
                              label: 'Movimentação',
                              icon: <BoltIcon className="h-5 w-5" />,
                              color: 'bg-gray-100 text-gray-600'
                            }
                        }
                      }

                      const actionInfo = getActionInfo(log.action)

                      return (
                        <div key={log.id} className="relative pb-8 last:pb-0">
                          {/* Ícone da Linha do Tempo */}
                          <div
                            className={`absolute left-0 flex h-10 w-10 items-center justify-center rounded-full border-4 border-white shadow-sm ${
                              isLatest ? 'bg-blue-600 text-white' : actionInfo.color
                            }`}
                          >
                            {actionInfo.icon}
                          </div>

                          {/* Conteúdo do Log */}
                          <div className="ml-14 pt-1">
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                              <h4 className={`text-sm font-bold ${isLatest ? 'text-blue-700' : 'text-gray-900'}`}>
                                {actionInfo.label}
                                {isLatest && (
                                  <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-800">
                                    Recente
                                  </span>
                                )}
                              </h4>
                              <span className="text-xs text-gray-400">
                                {formatDateTime(log.createdAt)}
                              </span>
                            </div>

                            <div className="mt-1.5 space-y-2">
                              {log.description && (
                                <p className="text-sm leading-relaxed text-gray-600">
                                  {log.description}
                                </p>
                              )}

                              {(log.previousStatus || log.status) && (
                                <div className="flex flex-wrap items-center gap-2">
                                  {log.previousStatus && (
                                    <div className="flex items-center gap-1.5 rounded-lg border border-gray-100 bg-gray-50 px-2 py-1">
                                      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">De</span>
                                      {getStatusBadgeModal(log.previousStatus)}
                                    </div>
                                  )}
                                  {log.previousStatus && log.status && (
                                    <svg className="h-4 w-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                    </svg>
                                  )}
                                  {log.status && (
                                    <div className="flex items-center gap-1.5 rounded-lg border border-gray-100 bg-gray-50 px-2 py-1">
                                      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Para</span>
                                      {getStatusBadgeModal(log.status)}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {log.performedBy && (
                              <div className="mt-3 flex items-center gap-2">
                                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                                  {log.performedBy === 'system' ? (
                                    <SystemIcon className="h-3.5 w-3.5" />
                                  ) : (
                                    <PersonIcon className="h-3.5 w-3.5" />
                                  )}
                                </div>
                                <span className="text-xs font-medium text-gray-500">
                                  Realizado por:{' '}
                                  <span className="text-gray-700">
                                    {log.performedBy === 'system' ? 'Sistema' : log.performedBy}
                                  </span>
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-50">
                      <HistoryIcon className="h-8 w-8 text-gray-300" />
                    </div>
                    <p className="text-center font-medium text-gray-500">
                      Nenhuma movimentação registrada ainda.
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'info' && (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Informações do Cliente */}
                <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900">Informações do Cliente</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500">Nome</p>
                      <p className="text-sm font-medium text-gray-900">{customerName}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500">E-mail</p>
                      <p className="text-sm text-gray-900">{customerEmail}</p>
                    </div>
                    {shippingAddress && (
                      <div>
                        <p className="mb-1 text-xs font-medium text-gray-500">
                          {isPickup ? 'Endereço de Cobrança' : 'Endereço de Entrega'}
                        </p>
                        <div className="rounded-lg bg-gray-50 p-3 text-sm">
                          <p className="text-gray-900">{shippingAddress.street}, {shippingAddress.number}</p>
                          {shippingAddress.complement && <p className="text-gray-700">{shippingAddress.complement}</p>}
                          <p className="text-gray-700">
                            {shippingAddress.neighborhood}, {shippingAddress.city} - {shippingAddress.state}
                          </p>
                          <p className="text-gray-700">CEP: {shippingAddress.zipCode}</p>
                        </div>
                      </div>
                    )}
                    {isPickup && (
                      <div className="rounded-lg bg-blue-50 p-3">
                        <div className="flex items-center gap-2">
                          <LocationIcon className="h-5 w-5 text-blue-600" />
                          <p className="text-sm font-medium text-blue-900">Retirada na Loja</p>
                        </div>
                        <p className="mt-1 text-xs text-blue-700">O cliente deve retirar os produtos no local físico</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Pagamento */}
                <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900">Pagamento</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-medium text-gray-500">Método</p>
                      <p className="text-sm font-medium text-gray-900">{getPaymentMethodLabelModal(order.paymentMethod)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500">Status</p>
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                          order.paymentStatus === 'PAID'
                            ? 'bg-green-100 text-green-800'
                            : order.paymentStatus === 'PENDING'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {order.paymentStatus === 'PAID'
                          ? 'Pago'
                          : order.paymentStatus === 'PENDING'
                          ? 'Pendente'
                          : order.paymentStatus}
                      </span>
                    </div>
                    {order.trackingCode && !isPickup && (
                      <div>
                        <p className="text-xs font-medium text-gray-500">Código de Rastreamento</p>
                        <p className="font-mono text-sm font-medium text-blue-600">{order.trackingCode}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {boletoTransaction && (
        <BoletoModal 
          isOpen={showBoletoModal}
          onClose={() => setShowBoletoModal(false)}
          boletoUrl={boletoTransaction.boletoUrl}
          boletoBarcode={boletoTransaction.boletoBarcode}
          dueDate={boletoTransaction.dueDate}
          amount={boletoTransaction.amount}
        />
      )}
    </Modal>
  )
}

export default AdminOrders
